import { Component } from '@theme/component';

/**
 * Custom Add To Cart Loading
 *
 * Purpose:
 * - Disable default fly-to-cart animation
 * - Show custom product thumbnail loading overlay immediately after Add To Cart click
 * - Prevent repeated Add To Cart clicks
 * - Keep <fly-to-cart> custom element registered
 */

const ADD_TO_CART_LOCK_TIME = 2600;
const LOADING_VISIBLE_TIME = 1800;

class FlyToCart extends Component {
  connectedCallback() {
    super.connectedCallback();

    /*
      Disable original fly-to-cart animation.
      Keep custom element registered to avoid breaking theme code.
    */
    this.remove();
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

(function setupCustomAddToCartLoading() {
  if (window.__customAddToCartLoadingLoaded) return;
  window.__customAddToCartLoadingLoaded = true;

  function getAddToCartButton(target) {
    return target.closest(
      [
        'button[name="add"]',
        '.add-to-cart-button',
        '[data-add-to-cart]',
        'button[type="submit"]'
      ].join(',')
    );
  }

  function isProductCartForm(button) {
    const form = button && button.closest('form');
    if (!form) return false;

    const action = form.getAttribute('action') || '';

    return (
      action.includes('/cart/add') ||
      !!form.querySelector('[name="id"]')
    );
  }

  function getProductImage() {
    return document.querySelector(
      [
        'media-gallery img',
        '.product-media-container img',
        '.product-media img',
        '.product__media img',
        '.product-gallery img',
        '[data-product-media] img',
        'main img'
      ].join(',')
    );
  }

  function getImageUrl(image) {
    if (!image) return '';

    return (
      image.currentSrc ||
      image.src ||
      image.getAttribute('src') ||
      ''
    );
  }

  function getImageCenter(image) {
    if (!image) {
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };
    }

    const rect = image.getBoundingClientRect();

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  function removeExistingOverlay() {
    const existingOverlay = document.querySelector('.custom-add-cart-loading-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
  }

  function showLoadingOverlay() {
    removeExistingOverlay();

    const image = getProductImage();
    const imageUrl = getImageUrl(image);
    const center = getImageCenter(image);

    const overlay = document.createElement('div');
    overlay.className = 'custom-add-cart-loading-overlay';
    overlay.style.setProperty('--custom-loading-x', `${center.x}px`);
    overlay.style.setProperty('--custom-loading-y', `${center.y}px`);

    overlay.innerHTML = `
      <div class="custom-add-cart-loading-overlay__inner">
        <div class="custom-add-cart-loading-overlay__image-wrap">
          ${
            imageUrl
              ? `<img class="custom-add-cart-loading-overlay__image" src="${imageUrl}" alt="" loading="eager">`
              : ''
          }
        </div>
        <div class="custom-add-cart-loading-overlay__text">loading...</div>
      </div>
    `;

    document.body.appendChild(overlay);

    window.setTimeout(function () {
      overlay.classList.add('custom-add-cart-loading-overlay--hidden');

      window.setTimeout(function () {
        overlay.remove();
      }, 250);
    }, LOADING_VISIBLE_TIME);
  }

  function setButtonLoading(button) {
    if (!button) return false;

    if (button.dataset.cartActionLoading === 'true') {
      return false;
    }

    button.dataset.cartActionLoading = 'true';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('custom-cart-action-loading');

    return true;
  }

  function resetButton(button) {
    if (!button) return;

    button.dataset.cartActionLoading = 'false';
    button.removeAttribute('aria-disabled');
    button.classList.remove('custom-cart-action-loading');
  }

  document.addEventListener(
    'click',
    function (event) {
      const button = getAddToCartButton(event.target);
      if (!button) return;

      if (!isProductCartForm(button)) return;

      /*
        Prevent repeated Add To Cart clicks.
      */
      if (button.dataset.cartActionLoading === 'true') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const locked = setButtonLoading(button);

      if (!locked) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      /*
        Show custom loading immediately.
        Do not block original Shopify/theme add-to-cart logic.
      */
      showLoadingOverlay();

      window.setTimeout(function () {
        resetButton(button);
      }, ADD_TO_CART_LOCK_TIME);
    },
    true
  );
})();